/**
 * Every call the portal makes to the API, with the token handling in one place.
 *
 * The refresh rule is the part worth reading. A 401 triggers exactly one refresh attempt, and
 * concurrent 401s share it rather than each starting their own — six panels on the dashboard
 * hitting an expired token at once must not fire six refreshes, because Supabase rotates the
 * refresh token on use and five of the six would then be spending a token that had already been
 * replaced. That is the shape of "it signs me out randomly".
 */

import {
  clearSession,
  deviceDetails,
  deviceId,
  loadSession,
  saveSession,
  type StoredSession,
} from '../auth/session';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Raised when the session is gone for good and the operator has to sign in again. */
export class SignedOutError extends ApiError {
  constructor(message = 'Your session has expired. Sign in again.') {
    super(401, message);
    this.name = 'SignedOutError';
  }
}

async function readError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === 'string') return body.detail;
    // FastAPI's validation errors arrive as a list of objects; the first message is the useful one.
    if (Array.isArray(body.detail) && body.detail.length > 0) {
      const first = body.detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
  } catch {
    /* A body that is not JSON tells us nothing the status has not already said. */
  }
  return fallback;
}

let refreshing: Promise<StoredSession | null> | null = null;

async function refreshSession(): Promise<StoredSession | null> {
  const current = loadSession();
  if (!current) return null;

  const response = await fetch('/api/v1/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: current.refresh_token, device: deviceDetails() }),
  });
  if (!response.ok) {
    clearSession();
    return null;
  }
  const session = (await response.json()) as StoredSession;
  saveSession(session);
  return session;
}

/** One refresh at a time, however many callers ask for it. */
function refreshOnce(): Promise<StoredSession | null> {
  refreshing ??= refreshSession().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Set internally to stop a retry from retrying. */
  retried?: boolean;
}

/**
 * One authenticated call, answered with the raw response.
 *
 * Split out from `request` so that anything which is not JSON — a PDF, today — goes through the
 * same token handling and the same one-refresh-at-a-time rule, rather than growing a second copy
 * of it that drifts.
 */
async function send(path: string, options: RequestOptions = {}): Promise<Response> {
  const session = loadSession();
  if (!session) throw new SignedOutError('Sign in to continue.');

  const headers: Record<string, string> = {
    Authorization: `Bearer ${session.access_token}`,
    'X-Device-ID': deviceId(),
  };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const response = await fetch(path, {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });

  if (response.status === 401 && !options.retried) {
    const renewed = await refreshOnce();
    if (!renewed) throw new SignedOutError();
    return send(path, { ...options, retried: true });
  }
  if (response.status === 401) {
    clearSession();
    throw new SignedOutError();
  }
  if (response.status === 404) {
    // The admin API answers 404 rather than 403 to anyone not on the operator allowlist, so a 404
    // on an admin path is far more likely to be "you are not an operator" than "no such customer".
    // Saying so beats leaving somebody to wonder why every screen is empty.
    throw new ApiError(404, await readError(response, 'Not found, or not yours to see.'));
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      await readError(response, `Request failed (${response.status}).`),
    );
  }
  return response;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/**
 * Fetch a file the API only serves to a signed-in operator, and hand it to the browser to save.
 *
 * A plain `<a href>` cannot do this: the endpoint wants a bearer token and an anchor carries none,
 * so the bytes are fetched here and turned into a local object URL the browser will download.
 */
export async function download(path: string, filename: string): Promise<void> {
  const response = await send(path);
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  // Released on the next tick rather than immediately, because the download has not necessarily
  // started reading the blob by the time click() returns.
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

export async function login(email: string, password: string): Promise<StoredSession> {
  const response = await fetch('/api/v1/auth/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, device: deviceDetails() }),
  });
  if (!response.ok) {
    throw new ApiError(response.status, await readError(response, 'Could not sign you in.'));
  }
  const session = (await response.json()) as StoredSession;
  saveSession(session);
  return session;
}

export async function logout(): Promise<void> {
  try {
    await request<void>('/api/v1/auth/logout', { method: 'POST' });
  } catch {
    // Signing out locally has to work even when the server cannot be reached. The refresh token is
    // then still live at Supabase until it expires, which is why the button says "Sign out" rather
    // than promising anything about other devices.
  }
  clearSession();
}
